<template>
  <div class="header-component contrast elevation-2" :class="{ 'px-1': !isDesktop }">
    <div v-if="!isDesktop && haveNavBar">
      <div class="px-2" @click="toggleSidebar">
        <BurgerIcon :active="mobileNavOpen" />
      </div>
    </div>
    <RouterLink v-if="isDesktop" :to="{ name: BUCKETS_PAGE }" class="logo-container px-4">
      <DynamicLogo
        class="logo"
        noBaseline
        :plan="
          isBucketsPage
            ? $store.getters.currentPlanType === 'enterprise'
              ? 'enterprise'
              : 'plus'
            : undefined
        "
        bigText
      />
    </RouterLink>

    <RouterLink v-else :to="{ name: BUCKETS_PAGE }" class="logo-mobile-container">
      <DynamicLogo
        class="logo-mobile"
        noLogo
        noBaseline
        :plan="
          isBucketsPage
            ? $store.getters.currentPlanType === 'enterprise'
              ? 'enterprise'
              : 'plus'
            : undefined
        "
      />
    </RouterLink>

    <div v-if="isBucketsPage && isDesktop" class="navigation-container">
      <div class="icon-heartbeat" title="Realtime Updates">
        <i class="heartbeat material-icons">sync_alt</i>
      </div>

      <!-- <div class="info-message" v-if="currentPlan.is_premium"> -->
      <!--   <VIcon small>mdi-alert</VIcon> Our email provider got a queuing issue at 10/15 12:00AM GMT+0. You might have received alert emails from yesterday. -->
      <!-- </div> -->
    </div>

    <div v-if="notifications.length" class="notifications-container">
      <KmTooltip v-for="(notification, index) in notifications" :key="index" bottom>
        <div
          slot="activator"
          v-nav-to="notification.link"
          class="notification ma-2"
          :class="{ clickable: Boolean(notification.link) }"
        >
          <VIcon>
            {{ notification.icon }}
          </VIcon>
          <span class="exclamation"> ! </span>
        </div>
        {{ notification.message }}
      </KmTooltip>
    </div>

    <VSpacer />

    <div v-if="isBucketsPage && currentPlan && isDesktop" class="bucket-meta-data-container mx-2">
      {{ processRatioString }}

      <span v-if="limitReached === true">
        <VBtn color="warning" small v-on:click="redirectToBilling()">
          Upgrade
          <VIcon>mdi-alert</VIcon>
        </VBtn>
      </span>

      <span class="ml-2">
        {{ currentPlan.name }}
      </span>
    </div>

    <VBtn
      v-if="isBucketsPage && isUserSuperAdmin && !isDedicated && isDesktop"
      class="ma-0 mr-2"
      small
      color="primary"
      target="_blank"
      :href="backOfficeURL"
    >
      BO
      <KmIcon name="km-icon-external-link" class="ml-2" :size="16" />
    </VBtn>

    <Menu
      v-if="
        isBucketsPage &&
        currentPlan &&
        currentPlan.name !== 'free' &&
        $store.getters.currentAppType === BACKEND_APP_TYPE &&
        isDesktop
      "
      :closeOnContentClick="false"
    >
      <VBtn slot="activator" small class="ma-0 mr-2" color="primary" @click="onConnectClick">
        <span>Connect</span>
        <VIcon :size="20" class="ml-2"> add_circle_outline </VIcon>
      </VBtn>
      <LinkInstructions disableTitle="true" class="pa-3 link-instructions-navbar" />
    </Menu>

    <Button v-if="!isDedicated" slot="activator" :small="!isDesktop" @click="onSearchDoc">
      <KmIcon :size="28" name="km-icon-search" />
    </Button>

    <Menu v-if="isBucketsPage" data-km-tests="settings-menu">
      <Button slot="activator" :small="!isDesktop">
        <KmIcon name="km-icon-settings" />
      </Button>
      <BucketSettingsEntries />
    </Menu>

    <Menu>
      <Button slot="activator" :small="!isDesktop">
        <VAvatar :size="28">
          <img :src="getGravatarUrl(user.email, 28)" alt="gravatar" />
        </VAvatar>
      </Button>
      <UserEntries />
    </Menu>
  </div>
</template>

<script>
import {
  BUCKETS_PAGE,
  BUCKET_META_PAGE,
  FRONTEND_MONITORING_DISABLED_PAGE,
  BILLING_PLANS_PAGE,
} from '@/router'
import countPayingProcesses from '@/utils/countPayingProcesses'
import LinkInstructions from '@/components/LinkInstructions'
import KmIcon from '@/components/KmIcon'
import KmTooltip from '@/components/KmTooltip'
import DynamicLogo from '@/components/DynamicLogo'
import SupportEntries from './SupportEntries'
import BucketSettingsEntries from './BucketSettingsEntries'
import UserEntries from './UserEntries'
import Menu from './Menu'
import { SET_IS_PORTAL_OPEN } from '@/store/mutations'
import Button from './Button'
import getGravatarUrl from '@/utils/gravatar'
import { FRONTEND_APP_TYPE, BACKEND_APP_TYPE } from '@/config/appTypes'
import { formatDate } from '@/utils/format'
import { trackConnectPopupViewed } from '@/tracking'

const INTERVAL_IN = 5000
const INTERVAL_OUT = 2000

export default {
  name: 'HeaderComponent',
  components: {
    LinkInstructions,
    KmIcon,
    BurgerIcon: () => import('./BurgerIcon.vue'),
    DynamicLogo,
    SupportEntries,
    UserEntries,
    BucketSettingsEntries,
    Button,
    Menu,
    KmTooltip,
  },
  props: {
    mobileNavOpen: {
      type: Boolean,
      default: false,
    },
    haveNavBar: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fillLogoBg: false,
      showBlacklist: false,
      blacklistInterval: false,
      subscription: null,
      intervalNbr: INTERVAL_IN,
      BUCKETS_PAGE,
      FRONTEND_MONITORING_DISABLED_PAGE,
      BUCKET_META_PAGE,
      FRONTEND_APP_TYPE,
      BACKEND_APP_TYPE,
    }
  },
  computed: {
    user() {
      return this.$store.getters.user
    },
    isDesktop() {
      return !!this.$vuetify.breakpoint.mdAndUp
    },
    currentPlan() {
      return this.$store.getters.currentPlan
    },
    limitReached() {
      var connectedProcs =
        countPayingProcesses(this.$store.getters.processes({ onlineOnly: true })) +
        this.blacklistedProcesses
      if (connectedProcs > this.currentPlan.process_limit) {
        return true
      }
      return false
    },
    processRatioString() {
      const processesCount = countPayingProcesses(
        this.$store.getters.processes({ onlineOnly: true }),
      )
      const processesCountLimit = this.currentPlan.process_limit
      return `${processesCount + this.blacklistedProcesses} / ${processesCountLimit} proc.`
    },
    isBucketsPage() {
      return this.$route.params.id !== undefined
    },
    blacklistedProcesses() {
      if (this.$store.getters.currentBucket === undefined) return
      return this.$store.getters.currentBucket.processBlacklist.length
    },
    backOfficeURL() {
      return this.$store.getters.backofficeURLs.bucket
    },
    isUserSuperAdmin() {
      return this.$store.getters.isUserSuperAdmin
    },
    isDedicated() {
      return this.$store.getters.isDedicated
    },
    notifications() {
      const result = []

      if (this.isBucketsPage && this.subscription) {
        if (this.subscription.cancel_at_period_end) {
          const date = formatDate(new Date(this.subscription.current_period_end * 1000))
          result.push({
            message: `Your subscription has been cancelled, your bucket will be downgraded on ${date}.`,
            icon: 'credit_card',
            link: {
              name: BILLING_PLANS_PAGE,
            },
          })
        }

        if (this.subscription.status === 'trialing') {
          const date = formatDate(new Date(this.subscription.trial_end * 1000))
          result.push({
            message: `This bucket is currently trialing the plan ${this.subscription.plan.nickname} until ${date}.`,
            icon: 'credit_card',
            link: {
              name: BILLING_PLANS_PAGE,
            },
          })
        }
      }

      if (this.user.email_token) {
        result.push({
          message: "You didn't validate your account email.",
          icon: 'email',
        })
      }

      return result
    },
  },
  watch: {
    blacklistedProcesses: {
      handler(value) {
        if (this.isBucketsPage === false) return
        if (value <= 0 && this.blacklistInterval === true) {
          this.blacklistInterval = false
          this.showBlacklist = false
          return
        }
        if (value > 0 && this.blacklistInterval === false) {
          this.blacklistInterval = true
          this.showInterval()
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.$watch(
      () => this.$store.getters.currentBillingSubscription,
      (sub) => {
        sub.then((sub) => {
          this.subscription = sub
        })
      },
      { immediate: true },
    )
  },
  beforeDestroy() {
    this.blacklistInterval = false
  },
  methods: {
    getGravatarUrl,
    redirectToBilling() {
      this.$router.push({
        name: BILLING_PLANS_PAGE,
      })
    },
    onSearchDoc() {
      this.$store.commit(SET_IS_PORTAL_OPEN, { isOpen: true, initialInput: 'doc ' })
    },
    onConnectClick() {
      console.log('[Header] Connect button clicked')
      trackConnectPopupViewed()
    },
    toggleSidebar() {
      console.log('toggleSidebar')
      this.$emit('toggleSidebar')
    },
    showInterval() {
      if (this.blacklistInterval === false) return

      this.showBlacklist = !this.showBlacklist
      this.intervalNbr = this.showBlacklist === true ? INTERVAL_OUT : INTERVAL_IN
      setTimeout(() => {
        this.showInterval()
      }, this.intervalNbr)
    },
  },
}
</script>

<style lang="postcss" scoped>
.header-component {
  & .info-message {
    padding: 15px;
    color: #ffcc00;
    font-weight: bold;
  }
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--headerHeight);
  z-index: 10; /* below navbar */
  position: relative;

  & .logo {
    height: calc(var(--headerHeight) * 0.6);
    width: auto;
  }

  & .logo-mobile-container {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
  }

  & .logo-mobile {
    height: calc(var(--headerHeight) * 0.4);
    width: auto;
  }

  & .navigation-container {
    display: flex;
  }

  & .bucket-meta-data-container {
    white-space: nowrap;
    align-self: center;
  }

  & .v-btn {
    align-self: center;
  }

  & .v-icon {
    color: inherit;
  }

  & .icon-heartbeat {
    width: clamp(28px, 4vw, 40px);
    margin-top: clamp(6px, 1.2vw, 13px);
    & .heartbeat {
      display: none;
    }
  }

  & .logo-container {
    display: flex;
    align-items: center;
    & svg {
      display: block;
      height: clamp(18px, 3vw, 22px);
    }
  }
  & .notifications-container {
    display: flex;
  }

  & .notification {
    display: flex;
    align-items: center;
    color: var(--warning);
    cursor: default;
    position: relative;

    &.clickable {
      cursor: pointer;
    }

    & .exclamation {
      position: absolute;
      top: 4px;
      right: -4px;
      font-weight: bold;
    }
  }
}

.link-instructions-component {
  width: 800px;
}
</style>
